import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { Plus, Mail, BarChart3, Clock, XCircle, Pause, Play, RotateCcw, AlertTriangle } from "lucide-react";
import { StatusFilter, filterItems } from "@/components/StatusFilter";
import { CampaignSearch } from "@/components/CampaignSearch";

const STATUS_COLORS = {
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  sending: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  queued: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  scheduled: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  cancelled: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  paused: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  error: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

function ReportDialog({ campaignId, onClose }) {
  const [report, setReport] = useState(null);
  useEffect(() => {
    if (campaignId) api.get(`/email-campaigns/${campaignId}/report`).then((r) => setReport(r.data)).catch(() => {});
  }, [campaignId]);

  return (
    <Dialog open={!!campaignId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{report?.campaign?.name || "Campaign report"}</DialogTitle></DialogHeader>
        {report && (
          <div className="space-y-5">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
              {[
                ["Sent", report.campaign.sent, "text-slate-100"],
                ["Failed", report.campaign.failed, "text-rose-400"],
                ["Opens", report.opens, "text-indigo-400"],
                ["Open %", `${report.open_rate}%`, "text-indigo-400"],
                ["Clicks", report.clicks, "text-emerald-400"],
                ["Unsubs", report.unsubscribes, "text-amber-400"],
              ].map(([l, v, c]) => (
                <div key={l} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
                  <div className={`font-heading text-lg font-bold ${c}`} data-testid={`report-stat-${l.toLowerCase().replace(" ", "-")}`}>{v}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{l}</div>
                </div>
              ))}
            </div>
            {report.campaign.error && <p className="text-sm text-rose-400">Error: {report.campaign.error}</p>}
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Per-contact activity</p>
              <div className="rounded-lg border border-slate-800 overflow-hidden max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
                    {["Recipient", "Status", "Opened", "Clicked"].map((h) => <TableHead key={h} className="text-slate-400 text-xs">{h}</TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.recipients.map((r, i) => (
                      <TableRow key={i} className="border-slate-800 hover:bg-slate-800/40" data-testid="report-recipient-row">
                        <TableCell className="text-slate-300 text-xs">{r.name || r.email}<div className="text-slate-500 text-[10px]">{r.email}</div></TableCell>
                        <TableCell><Badge className={`border text-[10px] ${r.status === "sent" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-rose-500/15 text-rose-400 border-rose-500/30"}`}>{r.status}</Badge></TableCell>
                        <TableCell className="text-xs">{r.opened ? <span className="text-indigo-400">✓</span> : <span className="text-slate-600">—</span>}</TableCell>
                        <TableCell className="text-xs">{r.clicked ? <span className="text-emerald-400">✓</span> : <span className="text-slate-600">—</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EmailCampaignsPage() {
  const [items, setItems] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/email-campaigns").then((r) => setItems(r.data)).catch(() => setItems([]));

  const cancel = async (id) => {
    try {
      await api.post(`/email-campaigns/${id}/cancel`);
      toast.success("Campaign cancelled");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const pause = async (id) => {
    try {
      await api.post(`/email-campaigns/${id}/pause`);
      toast.success("Campaign paused");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const resume = async (id) => {
    try {
      await api.post(`/email-campaigns/${id}/resume`);
      toast.success("Campaign resumed — continuing from where it stopped");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1">Sent via your xSMTP credentials</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Email Campaigns</h1>
        </div>
        <Button data-testid="new-email-campaign-button" onClick={() => navigate("/email-campaigns/new")} className="bg-indigo-500 hover:bg-indigo-600 text-white">
          <Plus size={15} className="mr-1.5" /> New campaign
        </Button>
      </div>

      {items === null ? null : items.length === 0 ? (
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-12 text-center" data-testid="email-campaigns-empty">
          <Mail size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No email campaigns yet. Design one and send it to an audience.</p>
        </div>
      ) : (
        <>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <StatusFilter items={items} value={filter} onChange={setFilter} accent="indigo" />
          <CampaignSearch value={search} onChange={setSearch} testId="email-campaign-search" />
        </div>
        {filterItems(items, filter, search).length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="email-campaigns-filter-empty">No campaigns match{search ? ` "${search}"` : " this status"}.</p>
        ) : (
        <div className="space-y-3">
          {filterItems(items, filter, search).map((c) => {
            const pct = c.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0;
            return (
              <div key={c.id} data-testid="email-campaign-row" className="stat-card p-5 fade-up">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <div>
                    <div className="font-heading font-semibold text-slate-100">{c.name}</div>
                    <div className="text-xs text-slate-500">"{c.subject}" → {c.audience_name} · {new Date(c.created_at).toLocaleString()}</div>
                    {c.status === "scheduled" && c.scheduled_at && (
                      <div data-testid="email-campaign-scheduled-time" className="text-xs text-blue-400 flex items-center gap-1 mt-0.5">
                        <Clock size={11} /> Scheduled for {new Date(c.scheduled_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`border ${STATUS_COLORS[c.status] || ""}`} data-testid="email-campaign-status">{c.status}</Badge>
                    {["queued", "sending"].includes(c.status) && (
                      <Button data-testid="email-campaign-pause-button" size="sm" onClick={() => pause(c.id)} className="bg-sky-500 hover:bg-sky-600 text-white h-8">
                        <Pause size={13} className="mr-1.5" /> Pause
                      </Button>
                    )}
                    {["paused", "cancelled", "error"].includes(c.status) && (
                      <Button data-testid="email-campaign-resume-button" size="sm" onClick={() => resume(c.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white h-8">
                        <Play size={13} className="mr-1.5" /> Resume
                      </Button>
                    )}
                    {["scheduled", "queued", "sending", "paused"].includes(c.status) && (
                      <Button data-testid="email-campaign-cancel-button" size="sm" variant="outline" onClick={() => cancel(c.id)} className="border-slate-700 text-rose-400 hover:bg-slate-800 h-8">
                        <XCircle size={13} className="mr-1.5" /> {c.status === "scheduled" ? "Cancel" : "Stop"}
                      </Button>
                    )}
                    {!["scheduled", "queued"].includes(c.status) && (
                      <Button data-testid="email-campaign-report-button" size="sm" variant="outline" onClick={() => setReportId(c.id)} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8">
                        <BarChart3 size={13} className="mr-1.5" /> Report
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={pct} className="h-1.5 bg-slate-800 flex-1" />
                  <span className="text-xs font-mono text-slate-400 shrink-0" data-testid="email-campaign-progress">
                    {c.sent} sent{c.failed ? ` · ${c.failed} failed` : ""} / {c.total}{c.total ? ` (${pct}%)` : ""}
                  </span>
                </div>
                {c.error && (
                  <div data-testid="email-campaign-error" className="flex items-start gap-1.5 mt-2 text-xs text-rose-400">
                    <AlertTriangle size={13} className="shrink-0 mt-0.5" /> <span>{c.error}</span>
                  </div>
                )}
                {c.recoveries > 0 && (
                  <div data-testid="email-campaign-recovered" className="flex items-center gap-1.5 mt-1 text-[11px] text-amber-400/90">
                    <RotateCcw size={11} /> Auto-recovered {c.recoveries}× after interruption — continued from last sent contact
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
        </>
      )}
      <ReportDialog campaignId={reportId} onClose={() => setReportId(null)} />
    </div>
  );
}
